<script lang="ts">
  import { meetingStore, costStore } from '../stores/meetingStore';
  import { i18nStore } from '../stores/i18nStore';
  import { formatCurrency } from '../utils/formatters';
  import { _ } from 'svelte-i18n';
  import { IconPlayerPlay, IconPlayerStop, IconRefresh, IconUsers, IconCurrencyDollar, IconSettings } from '@tabler/icons-svelte';
  import { slide } from 'svelte/transition';
  import Timer from './Timer.svelte';
  import InputField from './InputField.svelte';
  import CurrencySelector from './CurrencySelector.svelte';
  import LanguageSwitcher from './LanguageSwitcher.svelte';
  import ThemeToggle from './ThemeToggle.svelte';
  import ThemeSettings from './ThemeSettings.svelte';
  import Footer from './Footer.svelte';
  import Impressum from './pages/Impressum.svelte';
  import Datenschutz from './pages/Datenschutz.svelte';
  import Licenses from './pages/Licenses.svelte';

  let attendees = 5;
  let hourlyRate = 200;
  let currentLanguage = 'en';
  let settingsOpen = false;
  let impressumOpen = false;
  let datenschutzOpen = false;
  let licensesOpen = false;

  i18nStore.subscribe((lang) => {
    currentLanguage = lang;
  });

  meetingStore.subscribe((state) => {
    attendees = state.attendees;
    hourlyRate = state.hourlyRate;
  });

  function handleStart() {
    meetingStore.startTimer();
  }

  function handleStop() {
    meetingStore.stopTimer();
  }

  function handleReset() {
    meetingStore.resetTimer();
  }

  function handleAttendeesChange(event: CustomEvent<number>) {
    meetingStore.updateAttendees(event.detail);
  }

  function handleHourlyRateChange(event: CustomEvent<number>) {
    meetingStore.updateHourlyRate(event.detail);
  }
</script>

<div class="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-slate-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900 flex flex-col items-center justify-center p-4 py-8 transition-colors duration-300">
  <div class="w-full max-w-4xl flex-1 flex flex-col">
    <!-- Header with Language Switcher, Theme Toggle, and Settings -->
    <div class="flex justify-end gap-3 mb-6">
      <button
        on:click={() => settingsOpen = true}
        class="glass-button flex items-center gap-2 px-4 py-2 rounded-lg transition-all cursor-pointer"
        aria-label="Open theme settings"
        title="Theme Settings"
      >
        <IconSettings class="w-5 h-5" />
      </button>
      <ThemeToggle />
      <LanguageSwitcher />
    </div>

    <!-- Main Calculator Card -->
    <div class="glass-card p-8 md:p-12 rounded-3xl shadow-2xl">
      <h1 class="text-3xl md:text-4xl font-bold text-custom-heading dark:text-white text-center mb-8">
        {$_(`app.title`)}
      </h1>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        <!-- Input Fields -->
        <div class="space-y-6">
          <InputField
            label={$_(`calculator.attendees`)}
            value={attendees}
            min={1}
            step={1}
            icon={IconUsers}
            on:change={(e) => handleAttendeesChange(e)}
          />
          
          <InputField
            label={$_(`calculator.hourlyRate`)}
            value={hourlyRate}
            min={0}
            step={1}
            icon={IconCurrencyDollar}
            on:change={(e) => handleHourlyRateChange(e)}
          />
          
          <CurrencySelector />
        </div>

        <!-- Timer and Cost Display -->
        <div class="flex flex-col items-center justify-center space-y-6 min-w-0">
          <div class="w-full">
            <Timer
              elapsedSeconds={$meetingStore.elapsedSeconds}
              isRunning={$meetingStore.isRunning}
            />
          </div>
          
          <div class="text-center w-full">
            <div class="text-sm text-custom-secondary dark:text-white/70 mb-2 flex items-center justify-center gap-2">
              <IconCurrencyDollar class="w-4 h-4 icon-accent" />
              {$_(`calculator.cost`)}
            </div>
            <div class="text-3xl md:text-4xl lg:text-5xl font-bold font-mono text-green-custom dark:text-green-400 break-words">
              {formatCurrency($costStore, $meetingStore.currency, currentLanguage)}
            </div>
          </div>
        </div>
      </div>

      <!-- Control Buttons -->
      <div class="flex flex-wrap gap-4 justify-center">
        {#if !$meetingStore.isRunning}
          <button
            on:click={handleStart}
            class="glass-button btn-accent flex items-center gap-2 px-8 py-4 rounded-lg dark:bg-green-500/20 backdrop-blur-md border dark:border-green-400/30 text-white font-semibold dark:hover:bg-green-500/30 transition-all shadow-lg hover:shadow-xl"
          >
            <IconPlayerPlay class="w-5 h-5" />
            {$_(`calculator.start`)}
          </button>
        {:else}
          <button
            on:click={handleStop}
            class="glass-button flex items-center gap-2 px-8 py-4 rounded-lg bg-red-600 dark:bg-red-500/20 backdrop-blur-md border border-red-700 dark:border-red-400/30 text-white font-semibold hover:bg-red-700 dark:hover:bg-red-500/30 transition-all shadow-lg hover:shadow-xl"
          >
            <IconPlayerStop class="w-5 h-5" />
            {$_(`calculator.stop`)}
          </button>
        {/if}
        
        <button
          on:click={handleReset}
          class="glass-button flex items-center gap-2 px-8 py-4 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl text-custom dark:text-white"
        >
          <IconRefresh class="w-5 h-5" />
          {$_(`calculator.reset`)}
        </button>
      </div>
    </div>

    <!-- Footer -->
    <Footer
      on:toggleImpressum={() => impressumOpen = !impressumOpen}
      on:toggleDatenschutz={() => datenschutzOpen = !datenschutzOpen}
      on:toggleLicenses={() => licensesOpen = !licensesOpen}
    />

    <!-- Impressum Section -->
    {#if impressumOpen}
      <div
        transition:slide={{ duration: 300 }}
        class="mt-6"
        aria-expanded={impressumOpen}
      >
        <Impressum embedded={true} />
      </div>
    {/if}

    <!-- Datenschutz Section -->
    {#if datenschutzOpen}
      <div
        transition:slide={{ duration: 300 }}
        class="mt-6"
        aria-expanded={datenschutzOpen}
      >
        <Datenschutz embedded={true} />
      </div>
    {/if}

    <!-- Licenses Section -->
    {#if licensesOpen}
      <div
        transition:slide={{ duration: 300 }}
        class="mt-6"
        aria-expanded={licensesOpen}
      >
        <Licenses embedded={true} />
      </div>
    {/if}
  </div>
  
  <!-- Theme Settings Modal -->
  <ThemeSettings bind:isOpen={settingsOpen} />
</div>
